import { PropTypes } from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import API from "Boards/API";
import { useBoards } from "Boards/BoardContext";
import { BOARD_TYPES } from "Boards/const";
import { useSignups } from "Signups/state";
import classnames from "classnames";

import { HoverChild, HoverParent } from "UI/HoverChildren";

import useMutationObserver from "Utilities/useMutationObserver";

import SaveDefaultFilter from "./ControlRow/SaveDefaultFilter";
import useHasActiveFilters from "./ControlRow/hooks/useHasActiveFilters";
import useInitialActiveFilters from "./ControlRow/hooks/useInitialActiveFilter";
import { showSaveDefaultFiltersButton } from "./Utils/showSaveDefaultFiltersButton";
import { useSavedBoardFilter } from "./useSavedBoardFilter";

Filters.propTypes = {
  setIsFiltering: PropTypes.func,
};

export default function Filters({ setIsFiltering }) {
  const [
    {
      rowsUnfiltered,
      boardID,
      groupID,
      everyoneGroupID,
      columns,
      filtersPanelOpen,
      boardURL,
      groupURL,
      filteredTotal,
      unfilteredTotal,
      boardType,
      savedBoardFilter,
    },
    dispatch,
  ] = useBoards();

  const [{ currentSignupList }] = useSignups();
  const filterButtonRef = useRef();
  const [disabled, setDisabled] = useState(true);

  const { activeFiltersHaveChanged } = useInitialActiveFilters();

  const { show: showDefaultFilterSaveButton } = showSaveDefaultFiltersButton({
    savedBoardFilter,
    disabled,
    activeFiltersHaveChanged,
    boardType,
  });

  const { applySavedBoardFilter } = useSavedBoardFilter();

  const signupListID = currentSignupList.ID;
  const isReportBoard = boardType === BOARD_TYPES.REPORT;
  const isSignupBoard = boardType === BOARD_TYPES.SIGNUP;

  const [showingFiltersOverlay, setShowingFiltersOverlay] = useState(false);

  const [hasActiveFilters] = useHasActiveFilters();

  const allResultsReturned = filteredTotal === unfilteredTotal;

  const currentlyFiltering =
    (savedBoardFilter && hasActiveFilters) || rowsUnfiltered.length > 0;

  useEffect(() => {
    setIsFiltering(currentlyFiltering);
  }, [currentlyFiltering, setIsFiltering]);

  useEffect(() => {
    if (boardID === 0) return;
    refreshFilters();
    // Aug 2021 → we’re doing a clean slate of all eslint warnings- this is disabled because we don't have time to look at it.
    // eslint-disable-next-line
  }, [boardID, groupID, columns]);

  const filtersPanelContext = {
    populatePage: (data) => {
      dispatch({ type: "Update rowTotals", payload: data.rowTotals || {} });
      dispatch({ type: "Update Rows From Filters", payload: data.rows || [] });
      dispatch({
        type: "Update filteredTotal",
        payload: data.filteredTotal || 0,
      });
      dispatch({
        type: "Update unfilteredTotal",
        payload: data.unfilteredTotal,
      });
      window.fnReactBus.currentFilteredUsersInSheet = data.rows
        ? data.rows
            .filter((row) => row.entityType === "user") // this is used for bulk action note sending, so exclude non-users
            .map((row) => row.entityID)
        : [];
    },
    type: "sheets",
    sheetID: boardID,
    boardType: isSignupBoard ? "signup" : isReportBoard ? "report" : "group",
    signupListID: isSignupBoard ? signupListID : null,
    everyoneGroupID,
    sheets: [],
    showingFiltersPanel: () => dispatch({ type: "Filter Panel Open" }),
    hidingFiltersPanel: () => dispatch({ type: "Filter Panel Closed" }),
    clearFilterResults: () => {
      dispatch({ type: "Filters Cleared" });
      dispatch({ type: "Show Loading Overlay" });

      let getBoardCall = isSignupBoard
        ? API.getSignupBoard({ signupListID })
        : isReportBoard
        ? API.getReportBoard({ boardID, bypassSavedBoardFilter: true })
        : API.getGroupBoard({
            groupURL,
            boardURL,
            bypassSavedBoardFilter: true,
          });

      getBoardCall.post().then((boardData) => {
        if (boardData?.boardInfo?.ID !== boardID) return;
        dispatch({ type: "setBoardData", payload: boardData });
        dispatch({ type: "Hide Loading Overlay" });
      });
    },
  };

  const refreshFilters = () => {
    setDisabled(true);

    API.getFilters({ boardID, groupID })
      .post()
      .then(({ filterInfo, bulkActionDropDownItems, sheets }) => {
        // filterInfo is NOT the filters to run. It is the filter types that are possible.
        dispatch({ type: "Set Filters", payload: filterInfo });
        filtersPanelContext.sheets = sheets;

        if (
          !window.fnReactBus.filtersPanel ||
          window.fnReactBus.filtersPanel.context.sheetID !== boardID
        ) {
          window.fnReactBus.createFiltersPanelObj(
            filtersPanelContext,
            groupID,
            filterInfo,
            bulkActionDropDownItems
          );

          applySavedBoardFilter();
        } else {
          window.fnReactBus.updateFilterInfo(filterInfo);
        }
      })
      .finally(() => {
        setDisabled(false);
      });
  };

  const filterButtonClicked = () => {
    const filterCurrentlyInPlace = window.fnReactBus.getCurrentFilterRequest();

    if (filterCurrentlyInPlace && filtersPanelOpen) {
      window.fnReactBus.filtersPanel.closePanel();
    } else if (filterCurrentlyInPlace && !filtersPanelOpen) {
      window.fnReactBus.filtersPanel.showPanel();
      window.fnReactBus.filtersPanel.clearMaxHeight();
    } else {
      window.fnReactBus.filtersPanel.showNewFilterOverlay(
        filterButtonRef.current
      );
    }
  };

  // need this to see when the filter overlay is showing
  // Aug 2021 → we’re doing a clean slate of all eslint warnings- this is disabled because we don't have time to look at it.
  // eslint-disable-next-line
  const filterButtonClassListChanged = useCallback((mutations) => {
    setShowingFiltersOverlay(
      filterButtonRef.current.classList.contains("showing_overlay")
    );
  });

  useMutationObserver({
    target: filterButtonRef,
    options: { attributeFilter: ["class"] },
    callback: filterButtonClassListChanged,
  });

  const buttonContent = (() => {
    if (!currentlyFiltering) {
      return (
        <>
          <i className="fas fa-sliders-h-square" />
          &nbsp; Add a Filter
        </>
      );
    }

    return (
      <>
        <i className="fas fa-sliders-h-square" />
        &nbsp; Filtering{" "}
        <Bold className="sheets_filter_results_count">{filteredTotal}</Bold>
        &nbsp;of&nbsp;{unfilteredTotal}
      </>
    );
  })();

  const borderRadius = showDefaultFilterSaveButton ? "4px 0 0 4px" : "4px";

  return (
    <Wrapper $currentlyFiltering={currentlyFiltering}>
      <Content
        className={classnames({
          showBorder: !currentlyFiltering && showDefaultFilterSaveButton,
        })}
      >
        <HoverParent
          hoverStyle={{
            background: currentlyFiltering
              ? "rgba(0,0,0,.2)"
              : "rgba(0,0,0,.1)",
            borderRadius: 3,
          }}
          style={{
            borderRadius,
            border: "none",
          }}
        >
          <HoverChild forceHover={showingFiltersOverlay} disabled={disabled}>
            <ContentsWrapper
              className={classnames({
                filtering: currentlyFiltering,
                disabled: disabled,
              })}
            >
              <AddFiltersButton>
                <AddFiltersButtonInside
                  onClick={disabled ? () => {} : filterButtonClicked}
                  ref={filterButtonRef}
                  className="board_filters_button"
                >
                  {buttonContent}
                </AddFiltersButtonInside>
              </AddFiltersButton>
            </ContentsWrapper>
          </HoverChild>
        </HoverParent>
        {showDefaultFilterSaveButton && (
          <div
            style={{
              background: currentlyFiltering
                ? `linear-gradient(135deg, #be66cc 0%, 	#b653c6 100%)`
                : "transparent",
              borderRadius: "0 4px 4px 0",
              marginLeft: "-1px",
            }}
          >
            <SaveDefaultFilter currentlyFiltering={currentlyFiltering} />
          </div>
        )}
      </Content>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  border-radius: 4px;
`;

const Content = styled.div`
  display: flex;
  border-radius: 4px;

  &.showBorder {
    border: 1px solid #d8d8d8;
  }
`;

const ContentsWrapper = styled.div`
  display: flex;
  align-items: center;
  border-radius: 6px;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(135deg, #8b169c 0%, #9f35b0 100%);
    border: 1px solid transparent;
    z-index: 0;
    opacity: 0;
    transition: opacity 0.2s;
  }
  &.filtering::after {
    opacity: 1;
  }
  &.disabled {
    cursor: not-allowed;
    opacity: 0.75;
  }
`;

const AddFiltersButtonInside = styled.div`
  padding: 2px 6px 2px 3px;
  position: relative;
  z-index: 100;
  cursor: pointer;
  user-select: none;
  transition: scale 0.2s;
`;

const AddFiltersButton = styled.button`
  margin-left: 0;
  font-weight: 400;
  margin-left: 2px;
  padding-inline: 2px;
  position: relative;
  border: none;
  background: none;
  color: inherit;
  font-size: inherit;
  transition: scale 0.2s;

  &:hover ${AddFiltersButtonInside} {
    scale: 1.05;
  }
  &:active ${AddFiltersButtonInside} {
    scale: 1;
  }
`;

const Bold = styled.span`
  font-weight: 400;
`;
